import React from "react";
import { Award, FileText, Download, ExternalLink } from "lucide-react";
import { motion } from "framer-motion";
import PageLayout from "@/components/PageLayout";
import HeroSection from "@/components/HeroSection";
import Section from "@/components/Section";
import SectionHeader from "@/components/SectionHeader";
import { Button } from "@/components/Button";

// ============================================
// IMMAGINI
// ============================================

const HERO_IMAGE = "/images/99-castelli-header.webp";

// Immagini degli attestati (locali)
const ATTESTATO_AFFILIAZIONE = "/images/attestato-di-affiliazione.webp";
const CERTIFICATO_CSI = "/images/Certificato-CSI.webp";
const DOCUMENTO_AGGIUNTIVO = "/images/Documento-aggiuntivo.webp";

// ============================================
// ANIMAZIONI
// ============================================

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
    },
  },
};

// ============================================
// COMPONENTE
// ============================================

const Attestati: React.FC = () => {
  return (
    <PageLayout>
      {/* Hero Section */}
      <HeroSection
        title="Attestati"
        subtitle="Attestati ASD 99 Castelli e Statuto dell'Associazione"
        backgroundImage={HERO_IMAGE}
        height="medium"
      />

      {/* Attestati Gallery Section */}
      <Section background="white">
        <div className="max-w-6xl mx-auto">
          <SectionHeader
            title="Certificati e Attestati"
            subtitle="I documenti ufficiali che attestano la nostra affiliazione"
          />

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
          >
            {/* Attestato 1 */}
            <motion.div
              variants={fadeInUp}
              className="bg-surface rounded-xl overflow-hidden shadow-card border border-border hover:shadow-lg transition-shadow"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={ATTESTATO_AFFILIAZIONE}
                  alt="Attestato di Affiliazione ASD 99 Castelli"
                  className="w-full h-full object-contain bg-gray-50"
                />
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2 text-primary">
                  <Award size={20} />
                  <span className="font-semibold">Attestato di Affiliazione</span>
                </div>
              </div>
            </motion.div>

            {/* Attestato 2 */}
            <motion.div
              variants={fadeInUp}
              className="bg-surface rounded-xl overflow-hidden shadow-card border border-border hover:shadow-lg transition-shadow"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={CERTIFICATO_CSI}
                  alt="Certificato CSI ASD 99 Castelli"
                  className="w-full h-full object-contain bg-gray-50"
                />
              </div>
              <div className="p-4">
                <div className="flex items-center gap-2 text-primary">
                  <Award size={20} />
                  <span className="font-semibold">Certificato CSI</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </Section>

      {/* Statuto Section */}
      <Section background="gray">
        <div className="max-w-4xl mx-auto">
          <SectionHeader
            title="Atto Costitutivo e Statuto"
            subtitle="Il documento fondamentale della nostra associazione"
          />

          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-surface rounded-xl shadow-card border border-border overflow-hidden"
          >
            {/* PDF Preview */}
            <div className="aspect-[3/4] w-full">
              <iframe
                src="https://drive.google.com/file/d/1x2fftasrJBz5v0LuUFLrQXRMV4748lxu/preview"
                className="w-full h-full"
                title="Atto Costitutivo e Statuto Registrati"
                allow="autoplay"
              />
            </div>

            {/* Info e Download */}
            <div className="p-6 border-t border-border">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <FileText className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-text">Atto Costitutivo e Statuto Registrati</h3>
                    <p className="text-sm text-text-light">Documento PDF</p>
                  </div>
                </div>
                <Button
                  variant="primary"
                  size="md"
                  onClick={() =>
                    window.open("https://drive.google.com/file/d/1x2fftasrJBz5v0LuUFLrQXRMV4748lxu/view", "_blank")
                  }
                  className="inline-flex items-center gap-2"
                >
                  <Download size={18} />
                  Apri PDF
                  <ExternalLink size={16} />
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* Additional Document Section */}
      <Section background="white">
        <div className="max-w-4xl mx-auto">
          <SectionHeader title="Altri Documenti" subtitle="Documentazione aggiuntiva" />

          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-surface rounded-xl overflow-hidden shadow-card border border-border hover:shadow-lg transition-shadow"
          >
            <a
              href="https://drive.google.com/file/d/1Rs8PR2I5qyn81smiil-2mgBsGq8eBPJS/view?usp=drive_link"
              target="_blank"
              rel="noopener noreferrer"
              className="block"
            >
              <div className="aspect-[16/9] overflow-hidden">
                <img
                  src={DOCUMENTO_AGGIUNTIVO}
                  alt="Documento aggiuntivo"
                  className="w-full h-full object-contain bg-gray-50 hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-2 text-primary">
                  <FileText size={20} />
                  <span className="font-semibold">Visualizza documento</span>
                </div>
                <ExternalLink size={18} className="text-text-light" />
              </div>
            </a>
          </motion.div>
        </div>
      </Section>

      {/* CTA Section */}
      <Section background="primary" className="text-center">
        <motion.div
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="max-w-2xl mx-auto"
        >
          <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
            Hai bisogno di altri documenti?
          </h2>
          <p className="text-lg text-white/80 mb-8">
            Contattaci per richiedere copie o informazioni sui nostri documenti ufficiali.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="secondary"
              size="lg"
              onClick={() => (window.location.href = "mailto:99castelli@gmail.com")}
            >
              Contattaci via Email
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => (window.location.href = "/trasparenza")}
              className="border-white text-white hover:bg-white hover:text-primary"
            >
              Vai a Trasparenza
            </Button>
          </div>
        </motion.div>
      </Section>
    </PageLayout>
  );
};

export default Attestati;
